import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";
import { registerVerifyOTP } from "App/store/slices/auth.slice";

/**
 * Owns the session: the access token, the logged-in customer record and the
 * supplier's own tenant-agency id, which every tenant-scoped endpoint keys on.
 */

// portal:'supplier' — see loginSubmit in auth.slice. Enforced again at
// verification because that is the point a session is actually issued.
export const loginSubmitWithOTP = apiThunk<any, any>("user/loginSubmitWithOTP", {
  server: "login",
  url: "cust/login-success",
  method: "POST",
  body: (data) => ({ ...data, portal: "supplier" }),
  successMsg: true,
  errorMsg: true,
});

export const refreshToken = apiThunk<any, { token: string | null }>("user/refreshToken", {
  server: "login",
  url: "cust/mobile-refresh-token",
  method: "POST",
});

export const loadProfile = apiThunk<any, void>("user/loadProfile", {
  server: "login",
  url: "profile/me",
  method: "GET",
  errorMsg: true,
});

export const logoutRequest = apiThunk<any, void>("user/logout", {
  server: "login",
  url: "cust/logout",
  method: "POST",
});

const storeSessionData = (response: any) => {
  localStorage.setItem("refreshToken", response.refreshToken);
  localStorage.setItem("countryCode", response.data.user.country_code);
  localStorage.setItem("user", JSON.stringify(response.data.user));
  localStorage.setItem("userToken", response.data.access_token);
};

const clearSessionData = () => {
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("userToken");
};

interface UserState {
  /** null until the refresh-token round trip settles; gates the router. */
  refreshTokenLoaded: boolean | null;
  loggedIn: boolean;
  user: any;
  token: string | null;
  /** The caller's own agency. Every tenant-scoped URL is built from this. */
  tenantAgencyId: number | null;
  agency: any;
  profileData: any;
  currentLocation: any;
  zone: any;
  countryCode: string | null;
  loginError: string | null;
  errors: any;
  loading: boolean;
}

const initialState: UserState = {
  refreshTokenLoaded: null,
  loggedIn: false,
  user: null,
  token: null,
  tenantAgencyId: null,
  agency: null,
  profileData: null,
  currentLocation: null,
  zone: null,
  countryCode: localStorage.getItem("countryCode") || null,
  loginError: null,
  errors: null,
  loading: false,
};

/**
 * The supplier's agency id travels on the customer record. Until the tenant
 * endpoints ship (TENANT_AGENCY_MODULE.md §3.4 adds the column) the exact field
 * name is unconfirmed, so read the documented name and fall back to the nested
 * agency object rather than assuming one shape.
 */
const resolveAgency = (user: any) => ({
  tenantAgencyId: user?.tenant_agency_id ?? user?.agency?.id ?? null,
  agency: user?.agency ?? null,
});

/**
 * Whether a restored session belongs in this portal.
 *
 * The refresh-token endpoint is shared with the web and mobile apps and takes
 * no `portal` flag, so a rider who has signed in elsewhere on this browser
 * would otherwise have their session silently restored here. The server-side
 * checks cover the OTP login path; this covers the restore path.
 *
 * Not applied to registration's OTP step: rider_type only becomes 'supplier'
 * when step 3 creates the supplier record, so a mid-registration session is
 * legitimately still 'normal'.
 */
const isSupplierSession = (user: any) => user?.rider_type === "supplier";

/**
 * Take a successful auth response and become logged in. Login, registration's
 * OTP step and the refresh-token round trip all return the same envelope, so
 * they share this rather than repeating the six assignments three times.
 */
const adoptSession = (state: UserState, response: any) => {
  storeSessionData(response);
  const { tenantAgencyId, agency } = resolveAgency(response.data.user);
  state.user = response.data.user;
  state.token = response.data.access_token;
  state.loggedIn = true;
  state.tenantAgencyId = tenantAgencyId;
  state.agency = agency;
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentLocation: (state, action: PayloadAction<any>) => {
      state.currentLocation = action.payload;
    },
    /** Http.ts renewed the access token in the background (same envelope as login). */
    sessionRenewed: (state, action: PayloadAction<any>) => {
      if (action.payload?.status === "success" && action.payload?.data?.access_token) adoptSession(state, action.payload);
    },
    /** After Edit Profile: show the new name / photo everywhere, and keep it on reload. */
    patchUser: (state, action: PayloadAction<Record<string, any>>) => {
      state.user = { ...(state.user || {}), ...action.payload };
      try {
        localStorage.setItem("user", JSON.stringify(state.user));
      } catch {
        /* storage unavailable — the in-memory copy still updates */
      }
    },
    logout: (state) => {
      clearSessionData();
      state.user = null;
      state.token = null;
      state.loggedIn = false;
      state.tenantAgencyId = null;
      state.agency = null;
      state.profileData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginSubmitWithOTP.pending, (state) => {
        state.errors = null;
        state.loginError = null;
        state.user = null;
        state.token = null;
        state.loggedIn = false;
        state.loading = true;
      })
      .addCase(loginSubmitWithOTP.fulfilled, (state, action) => {
        const response = action.payload;
        state.loading = false;
        if (response?.status === "success") {
          adoptSession(state, response);
        } else {
          state.loginError = response?.msg ?? "Login failed.";
        }
      })
      .addCase(loginSubmitWithOTP.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      /**
       * Verifying a registration OTP logs the new account straight in — the
       * server returns the same {refreshToken, data:{access_token, user}} shape
       * as login. Adopting it here is what lets registration step 3 call the
       * authenticated register-profile endpoint.
       */
      .addCase(registerVerifyOTP.fulfilled, (state, action) => {
        const response = action.payload;
        if (response?.status === "success" && response?.data?.access_token) {
          adoptSession(state, response);
        }
      })

      .addCase(refreshToken.pending, (state) => {
        state.errors = null;
        state.refreshTokenLoaded = false;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        const res = action.payload;
        // Always mark the gate satisfied — an unauthenticated visitor must
        // still reach the login screen rather than sit on the loader forever.
        state.refreshTokenLoaded = true;
        if (res?.status === "success") {
          // Only a supplier's session may be restored here. A rider or driver
          // who signed in to the web app on this browser holds a valid refresh
          // token, and adopting it would let them straight into the portal.
          if (isSupplierSession(res?.data?.user)) {
            adoptSession(state, res);
          } else {
            clearSessionData();
          }
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.errors = action.payload;
        state.refreshTokenLoaded = true;
      })

      .addCase(loadProfile.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(loadProfile.fulfilled, (state, action) => {
        state.profileData = action.payload?.data;
        state.loading = false;
      })
      .addCase(loadProfile.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(logoutRequest.fulfilled, (state) => {
        clearSessionData();
        state.user = null;
        state.token = null;
        state.loggedIn = false;
        state.tenantAgencyId = null;
        state.agency = null;
        state.profileData = null;
      })
      // A failed logout call must still end the local session, otherwise a
      // network blip leaves the user apparently signed in.
      .addCase(logoutRequest.rejected, (state) => {
        clearSessionData();
        state.user = null;
        state.token = null;
        state.loggedIn = false;
        state.tenantAgencyId = null;
        state.agency = null;
      });
  },
});

export const { setCurrentLocation, logout, patchUser, sessionRenewed } = userSlice.actions;
export default userSlice.reducer;
